import Vue from 'vue'
import { effectiveWindow, parseWindowValue } from '@/modules/helpers/estimatedWindow'

import {
    ASAP,
    COURIER_CANCELLED,
    COURIER_DELIVERED,
    COURIER_ERROR,
    COURIER_FINISHED,
    COURIER_INITIALIZED,
    IN_PROGRESS_COURIER,
    ORDER_APPROVED,
    ORDER_CANCELLED,
    ORDER_CANCELLED_AFTER_APPROVAL,
    ORDER_NEW,
    PREORDER,
    RESTAURANT_MANAGED,
    THIRD_PARTY_TYPE_NON_PARTNER,
    SECTION_0,
    SECTION_1,
    SECTION_2,
    SECTION_3,
    SHOW_COURIER,
} from '@/modules/store/deliveryStatus/variables'

function generateGetters() {
    return {
        ASAP: () => ASAP,
        PREORDER: () => PREORDER,
        COURIER_CANCELLED: () => COURIER_CANCELLED,
        COURIER_ERROR: () => COURIER_ERROR,
        SECTION_0: () => SECTION_0,
        SECTION_1: () => SECTION_1,
        SECTION_2: () => SECTION_2,
        SECTION_3: () => SECTION_3,
        IN_PROGRESS_COURIER: () => IN_PROGRESS_COURIER,
        orderError: (state) => state.orderError,
        deliveryJob: ({ deliveryJob }) => {
            return {
                id: deliveryJob.id,
                created_at: deliveryJob.createdAt || deliveryJob.created_at,
                status: deliveryJob.status,
                courier_lat: deliveryJob.courierLat || deliveryJob.courier_lat,
                courier_long: deliveryJob.courierLong || deliveryJob.courier_long,
                eta_to_dropoff: deliveryJob.etaToDropoff || deliveryJob.eta_to_dropoff,
                courier_status: deliveryJob.courierStatus || deliveryJob.courier_status,
            }
        },
        unprocessedCanteenOrder: (state) =>
            state.trackOrder?.isCanteenSubOrder && state.trackOrder?.isDeliveryProcessed,
        deliveryStatus: (state) => state.deliveryStatus,
        showCourier: (state, getters) =>
            SHOW_COURIER.includes(state.deliveryStatus) && !getters.restaurantManaged,
        deliveryInitialized: (state) => state.deliveryStatus === COURIER_INITIALIZED,
        deliveryCancelled: (state) => state.deliveryStatus === COURIER_CANCELLED,
        deliveryError: (state) => state.deliveryStatus === COURIER_ERROR,
        deliveryFinished: (state, getters) =>
            state.deliveryStatus === COURIER_FINISHED && !getters.restaurantManaged,
        deliveryInProgress: (state, getters) =>
            IN_PROGRESS_COURIER.includes(state.deliveryStatus) && !getters.restaurantManaged,
        isDeliveryProcessed: (state) =>
            state.order.is_delivery_processed || state.trackOrder.isDeliveryProcessed,
        isThirdPartyOrder: ({ trackOrder }) =>
            (!!trackOrder?.restaurantThirdPartyType &&
                trackOrder.restaurantThirdPartyType !== THIRD_PARTY_TYPE_NON_PARTNER) ||
            !!trackOrder?.thirdPartyOrderId ||
            !!trackOrder?.thirdPartyTrackingUrl ||
            !!trackOrder?.thirdPartyCode,
        restaurantManaged: (state, getters) =>
            state.deliveryStatus === RESTAURANT_MANAGED ||
            (!state.deliveryStatus && getters.isDeliveryProcessed),
        // the restaurant the page loaded first, the order's own restaurant object as a fallback; never undefined,
        // which would leave the raw {restaurant} placeholder in the text
        restaurantName: (state) => state.restaurant?.name || state.order?.restaurant?.name || '',
        order: (state) => state.order,
        restaurant: (state) => state.restaurant,
        trackOrder: (state) => state.trackOrder,
        waitingForUpdate: (state) => state.waitingForUpdate,
        orderNew: (state) => state.order?.order_status === ORDER_NEW,
        orderApproved: (state) => state.order?.order_status === ORDER_APPROVED,
        orderCancelled: (state) => {
            return state.order?.order_status === ORDER_CANCELLED
        },
        orderCancelledByUser: (state, getters) => {
            return getters.orderCancelled && state.order?.cancel_source === 'user'
        },
        orderCancelledAfterApprove: (state) =>
            state.order?.order_status === ORDER_CANCELLED_AFTER_APPROVAL,
        orderProcessing: (state, getters) =>
            (getters.deliveryInitialized || !getters.orderApproved) &&
            !getters.deliveryCancelled &&
            !(getters.orderCancelled || getters.orderCancelledAfterApprove),
        statusKey: (state, getters) => {
            if (getters.orderProcessing) return undefined
            if (getters.orderCancelledByUser) return 'order_cancelled_user'
            else if (state.orderError) return 'payment_error'
            else if (getters.orderCancelled) return 'order_cancelled'
            else if (getters.orderCancelledAfterApprove) return 'order_cancelled'
            else if (getters.deliveryFinished) return 'finished'
            else if (getters.isThirdPartyOrder) return 'third_party'
            else if (getters.restaurantManaged) return 'restaurant_managed'
            else return undefined
        },
        courierStatusText: (state, getters) => {
            // The courier is being replaced, the customer only sees that an update is coming (tasks#151).
            if (
                [COURIER_CANCELLED, COURIER_ERROR].includes(state.deliveryStatus) &&
                !getters.orderCancelled &&
                !getters.orderCancelledAfterApprove
            ) {
                return Vue.i18n.translate('enums.delivery_update')
            }
            return state.deliveryStatus
                ? Vue.i18n.translate(
                      `enums.courier_status.${state.deliveryStatus}${
                          [...SECTION_0, ...SECTION_1, COURIER_ERROR].includes(state.deliveryStatus)
                              ? `.${state.order.asap ? ASAP : PREORDER}`
                              : ''
                      }`,
                      { restaurant: getters.restaurantName }
                  )
                : getters.unprocessedCanteenOrder
                ? Vue.i18n.translate('enums.canteen_status.unprocessed', {
                      cutoffTime: Vue.options.filters.toDateTime(
                          state.trackOrder?.canteenCutoffTime
                      ),
                  })
                : Vue.i18n.translate(
                      `enums.courier_status.initialized.${state.order.asap ? ASAP : PREORDER}`,
                      { restaurant: getters.restaurantName }
                  )
        },
        // tasks#202: the server's estimated window (Onfleet), null without one
        estimatedWindow: (state, getters, rootState, rootGetters) => {
            const deliveryJob = state.deliveryJob || {}
            const timezone = rootGetters['site/timezone']
            const from = parseWindowValue(deliveryJob.estimatedWindowFrom, timezone)
            const to = parseWindowValue(deliveryJob.estimatedWindowTo, timezone)
            // shown in the browser's zone like every other time on the page
            return from && to ? { from: from.local(), to: to.local() } : null
        },
        // delivered / cancelled: the stored window is history, the page shows its existing end state (spec §4.8)
        estimatedWindowTerminal: (state, getters) =>
            getters.orderCancelled ||
            getters.orderCancelledAfterApprove ||
            [COURIER_DELIVERED, COURIER_FINISHED].includes(state.deliveryStatus) ||
            ['finished', 'cancelled'].includes(state.deliveryJob?.status),
        // the window to show: never in the past against the reactive clock (spec §4.7), null when none applies
        effectiveEstimatedWindow: (state, getters) => {
            if (!getters.estimatedWindow || getters.estimatedWindowTerminal) return null
            return effectiveWindow(
                getters.estimatedWindow.from,
                getters.estimatedWindow.to,
                Vue.moment(state.now)
            )
        },
        deliveryWindowMinutes: (state) => {
            const order = state.order || {}
            // The public tracking payload carries the window the admin shows
            // (core-api DeliveryWindowCalculator); the logged-in order payload
            // does not, so apply the same rule: ASAP orders get the spread
            // between the address' min and max delivery time (15-45 minutes
            // by default), pre-orders and canteen orders the 30-minute
            // delivery period.
            if (order.deliveryWindowFrom && order.deliveryWindowTo) {
                return Vue.moment(order.deliveryWindowTo).diff(
                    Vue.moment(order.deliveryWindowFrom),
                    'minutes'
                )
            }
            if (!order.asap) return Vue.prototype.$deliveryPeriod
            const details = order.delivery_address_details || {}
            const min = details.min_delivery_time == null ? 15 : details.min_delivery_time
            const max = details.max_delivery_time == null ? 45 : details.max_delivery_time
            return Math.abs(max - min)
        },
        deliveryTime: (state, getters, rootState, { locale }) => {
            const window = getters.effectiveEstimatedWindow
            const deliveryTime = window
                ? window.from
                : state.deliveryJob?.eta_to_dropoff ||
                  state.order.deliveryWindowFrom ||
                  state.order.required_delivery_time
            const deliveryTimeEnd = window
                ? window.to
                : Vue.moment(deliveryTime).add(getters.deliveryWindowMinutes, 'minutes')

            const range =
                window || getters.deliveryWindowMinutes > 0
                    ? `${Vue.options.filters.toHour(deliveryTime)}-${deliveryTimeEnd.format(
                          'HH:mm'
                      )}`
                    : Vue.options.filters.toHour(deliveryTime)

            if (Vue.moment().isSame(deliveryTime, 'day')) {
                return range
            } else if (Vue.moment().add(1, 'days').isSame(deliveryTime, 'day')) {
                return Vue.i18n.translate('labels.tomorrow', {
                    time: range,
                })
            } else {
                return `${Vue.moment(deliveryTime).format(
                    locale === 'hu'
                        ? Vue.prototype.$dayMonthFormat_1_HU
                        : Vue.prototype.$dayMonthFormat_1_EN
                )} ${range}`
            }
        },
    }
}

export default generateGetters
